import {
  fetchAdverts,
  fetchCurrentEvent,
  fetchDiscounts,
  fetchSlides,
  fetchStore,
  fetchTopOffers,
  fetchTranslations,
} from "@/api/api-queries";
import HomePage from "@/components/Home";
import { getDomainLanguage } from "@/hooks/getDomain";
import { useContext, useEffect, useState } from "react";
import { IoIosArrowUp } from "react-icons/io";
import { useRouter } from "next/router";
import Search from "../components/Search";
import { getAdProvider } from "@/lib/ad-config";
import { AppContext } from "./_app";

const loadAfterPageReady = (fn) => {
  if (typeof window === "undefined") return;

  const run = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => fn(), { timeout: 3000 });
    } else {
      setTimeout(fn, 2500);
    }
  };

  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
};

export default function Home({
  translation,
  offers,
  slides,
  advertisement,
  topStores,
  discounts,
  store,
  subdomain,
  domain,
  countryCode,
  siteID,
  canonicalUrl,
  currentEvent,
}) {
  const [localSubdomain, setLocalSubdomain] = useState(subdomain);
  const { setTranslation } = useContext(AppContext);
  const router = useRouter();

  useEffect(() => {
    setTranslation(translation);
  }, [translation, setTranslation]);

  useEffect(() => {
    setLocalSubdomain(subdomain || "www");
  }, [subdomain]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!siteID) return;

    const existingPixel = document.getElementById("mtmpxl");
    if (existingPixel) existingPixel.remove();

    const params = new URLSearchParams({
      idsite: siteID,
      rec: "1",
      url: window.location.href,
      action_name: document.title || localSubdomain || "www",
      urlref: sessionStorage.getItem("last_url") || document.referrer,
      apiv: "1",
    });

    const trackingPixel = document.createElement("img");
    trackingPixel.id = "mtmpxl";
    trackingPixel.src = `https://dashboard.imp-multimedia.com/matomo/matomo.php?${params.toString()}`;
    trackingPixel.style.border = "0";
    trackingPixel.style.position = "absolute";
    trackingPixel.style.left = "-9999px";
    trackingPixel.width = 1;
    trackingPixel.height = 1;
    trackingPixel.alt = "";

    document.body.appendChild(trackingPixel);
    sessionStorage.setItem("last_url", window.location.href);

    return () => {
      const oldPixel = document.getElementById("mtmpxl");
      if (oldPixel) oldPixel.remove();
    };
  }, [siteID, router.asPath, localSubdomain]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!siteID) return;

    loadAfterPageReady(() => {
      // Skip AdSense on ZeroPark traffic (zsem_data cookie set by ZP redirect)
      if (document.cookie.match(/zsem_data=([^;]+)/)) return;

      if (!document.getElementById("googleAdsRemarketing")) {
        const script = document.createElement("script");
        script.id = "googleAdsRemarketing";
        script.async = true;
        script.src =
          "https://www.googletagmanager.com/gtag/js?id=AW-10775638212";
        document.head.appendChild(script);

        script.onload = () => {
          window.dataLayer = window.dataLayer || [];

          function gtag() {
            window.dataLayer.push(arguments);
          }

          window.gtag = gtag;
          window["gtag_enable_tcf_support"] = true;

          gtag("js", new Date());
          gtag("config", "AW-10775638212", {
            cookie_domain: "none",
            cookie_flags: "SameSite=None;Secure",
          });
        };
      }

      // Skip AdSense if Refinery89 is the active ad provider for this domain
      if (getAdProvider(domain) !== "refinery89" && !document.getElementById("googleAdsScript")) {
        const googleScript = document.createElement("script");
        googleScript.id = "googleAdsScript";
        googleScript.async = true;
        googleScript.src =
          "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2665851518065203";
        googleScript.crossOrigin = "anonymous";
        document.head.appendChild(googleScript);
      }
    });
  }, [siteID]);

  const scrollToTop = () => {
    if (typeof window === "undefined") return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div className="w-full inner-container relative">
        {localSubdomain === "www" ? (
          <HomePage
            translation={translation}
            offers={offers}
            slides={slides}
            advertisement={advertisement}
            domain={domain}
            countryCode={countryCode}
            canonicalUrl={canonicalUrl}
          />
        ) : (
          <Search
            discounts={discounts}
            store={store}
            topStores={topStores}
            translation={translation}
            domain={domain}
            countryCode={countryCode}
            canonicalUrl={canonicalUrl}
            currentEvent={currentEvent}
          />
        )}

        <button
          onClick={scrollToTop}
          className="arrow-up inner-container"
          title="up"
        >
          <IoIosArrowUp />
        </button>
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  const { req, res } = context;

  const [domain, countryCode, siteID] = getDomainLanguage(req);

  const host = req.headers.host || "";
  const parts = host.split(".");

  const pathname = context.resolvedUrl?.split("?")[0] || "";
  const slug = pathname.replace(/^\/|\/$/g, "");

  // Only homepage subdomains are treated as merchant pages
  const subdomain =
    parts.length > 2 && parts[0] !== "www"
      ? parts[0]
      : "www";

  const pageType =
    subdomain !== "www"
      ? "store"
      : slug
        ? "event"
        : "home";

  const term =
    pageType === "store"
      ? subdomain
      : pageType === "event"
        ? slug
        : null;

  const type =
    pageType === "store"
      ? "store"
      : pageType === "event"
        ? "event"
        : null;

  // Check if this is a refresh request (purge API cache, don't cache this response)
  const isRefresh = new URL(req.url, `https://${req.headers.host}`).searchParams.has("refresh");

  // Default cache headers for successful responses
  const cacheSuccess = isRefresh
    ? "no-store, no-cache, must-revalidate, max-age=0"
    : "public, s-maxage=1, stale-while-revalidate=1800";

  const [translation, offers, slides, advertisement, currentEvent] = await Promise.all([
    fetchTranslations(req),
    fetchTopOffers(req),
    fetchSlides(req),
    fetchAdverts(req),
    fetchCurrentEvent(req),
  ]);

  const topStores = Array.isArray(offers)
    ? offers
        .slice(0, 3)
        .map((offer) => offer.offers && offer.offers[0])
        .filter(Boolean)
    : [];

  const store = term ? await fetchStore(req, term, type) : null;
  const hostWithoutPort = String(domain).replace(/:\d+$/, "");

  // Store not found → return 404 so Next.js renders pages/404.js
  // API returns {id: 0} for missing stores, not null
  // Don't cache 404s — the API might be temporarily down (502)
  if (term && (!store || !store.id || store.id === 0)) {
    res.setHeader("Cache-Control", "no-store, no-cache, must-revalidate, max-age=0");
    return {
      notFound: true,
    };
  }

  // Success — cache the response
  res.setHeader("Cache-Control", cacheSuccess);

  const discounts =
    store && store.id
      ? await fetchDiscounts(req, store.id, type)
      : [];

  const firstOfferId = discounts?.[0]?.id ? String(discounts[0].id) : "";

  const canonicalUrl =
    pageType === "event"
      ? `https://www.${hostWithoutPort}/${slug}`
      : subdomain === "www"
        ? `https://www.${hostWithoutPort}/`
        : `https://${subdomain}.${hostWithoutPort}/`;

  return {
    props: {
      translation,
      offers,
      slides,
      advertisement,
      topStores,
      discounts,
      store,
      subdomain,
      domain,
      countryCode,
      siteID,
      canonicalUrl,
      firstOfferId,
      currentEvent,
    },
  };
}